import * as React from "react";
import { Box, Button, Card, Grid, Stack, TextField, Typography, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

export interface MtUsageReportFilters {
  clientName?: string;
  clientId?: string;
  branch?: string;
}

interface Props {
  initialFilters?: MtUsageReportFilters;
  onFiltersChange: (filters: MtUsageReportFilters) => void;
}

enum FieldNames {
  clientName = "clientName",
  clientId = "clientId",
  branch = "branch"
}

const MtUsageReportFilter: React.FC<Props> = ({ initialFilters, onFiltersChange }) => {
  const [currentTextFilters, setCurrentTextFilters] = React.useState({
    clientName: initialFilters?.clientName || "",
    clientId: initialFilters?.clientId || "",
    branch: initialFilters?.branch || ""
  });

  const [debouncedTextFilters, setDebouncedTextFilters] = React.useState({
    clientName: initialFilters?.clientName || "",
    clientId: initialFilters?.clientId || "",
    branch: initialFilters?.branch || ""
  });

  React.useEffect(() => {
    const hasEmptyField = Object.values(currentTextFilters).some(value => value === "");
    const previouslyHadValues = Object.values(debouncedTextFilters).some(value => value !== "");

    if (hasEmptyField && previouslyHadValues) {
      setDebouncedTextFilters(currentTextFilters);
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedTextFilters(currentTextFilters);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [currentTextFilters, debouncedTextFilters]);

  const handleTextInputChange = React.useCallback((field: keyof typeof currentTextFilters, value: string) => {
    setCurrentTextFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  React.useEffect(() => {
    const filters: MtUsageReportFilters = {
      clientName: debouncedTextFilters.clientName || undefined,
      clientId: debouncedTextFilters.clientId || undefined,
      branch: debouncedTextFilters.branch || undefined
    };

    onFiltersChange(filters);
  }, [debouncedTextFilters, onFiltersChange]);

  const clearAllFilters = React.useCallback(() => {
    setCurrentTextFilters({ clientName: "", clientId: "", branch: "" });
    setDebouncedTextFilters({ clientName: "", clientId: "", branch: "" });
  }, []);

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Stack spacing={2}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Filter Report Data</Typography>
          <Button variant="outlined" size="small" startIcon={<ClearIcon />} onClick={clearAllFilters}>
            Clear All Filters
          </Button>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              name={FieldNames.clientName}
              label="Client Name"
              placeholder="Search client name..."
              size="small"
              fullWidth
              variant="outlined"
              value={currentTextFilters.clientName}
              onChange={e => handleTextInputChange("clientName", e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              name={FieldNames.clientId}
              label="Client ID"
              placeholder="Search client ID..."
              size="small"
              fullWidth
              variant="outlined"
              value={currentTextFilters.clientId}
              onChange={e => handleTextInputChange("clientId", e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              name={FieldNames.branch}
              label="Branch"
              placeholder="Search branch..."
              size="small"
              fullWidth
              variant="outlined"
              value={currentTextFilters.branch}
              onChange={e => handleTextInputChange("branch", e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </Stack>
    </Card>
  );
};

export { MtUsageReportFilter };
